import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { useWebSocket } from '../../shared/contexts/WebSocketContext';
import {
  adminDashboardService,
  DashboardStats
} from '../../services/admin/admin-dashboard.service';
import AdminNavBar from './AdminNavBar';
import './AdminDashboardPage.css';

// ── Helpers ────────────────────────────────────────────────────────────────

function shortDate(iso: string) {
  const parts = iso.split('-');
  return `${parts[1]}/${parts[2]}`;
}

// ── Live error feed entry ──────────────────────────────────────────────────

interface LiveErrorEntry {
  id: string;
  message: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  category: string;
  lastOccurrence: number;
}

// ── Stat card ──────────────────────────────────────────────────────────────

function StatCard({
  label,
  value,
  sub,
  alert
}: {
  label: string;
  value: string | number;
  sub?: string;
  alert?: boolean;
}) {
  return (
    <div className={`adm-stat-card${alert ? ' adm-stat-card--alert' : ''}`}>
      <div className='adm-stat-value'>{value}</div>
      <div className='adm-stat-label'>{label}</div>
      {sub && <div className='adm-stat-sub'>{sub}</div>}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────

export default function AdminDashboardPage() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [liveErrors, setLiveErrors] = useState<LiveErrorEntry[]>([]);
  const { socket } = useWebSocket();
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(async () => {
      setRefreshing(true);
      setError('');
      try {
        const data = await adminDashboardService.getStats();
        setStats(data);
      } catch {
        setError('Failed to load dashboard data');
      } finally {
        setRefreshing(false);
      }
    }, 30_000);
  }, []);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await adminDashboardService.getStats();
      setStats(data);
    } catch {
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
    // Reset the 30-second clock after every manual refresh
    startTimer();
  }, [startTimer]);

  // Initial fetch + 30-second auto-refresh
  useEffect(() => {
    void fetchStats();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [fetchStats]);

  // Live error feed via WebSocket
  useEffect(() => {
    if (!socket) return;
    const handleError = (entry: LiveErrorEntry) => {
      setLiveErrors((prev) => [entry, ...prev].slice(0, 20));
    };
    socket.on('admin:error', handleError);
    return () => {
      socket.off('admin:error', handleError);
    };
  }, [socket]);

  return (
    <div className='container-fluid adm-page'>
      <AdminNavBar />

      <div className='adm-toolbar'>
        <h4 className='adm-title'>Dashboard</h4>
        <button
          className='btn btn-sm btn-outline-secondary'
          onClick={() => void fetchStats()}
          disabled={loading || refreshing}
        >
          {loading || refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {error && <div className='alert alert-danger'>{error}</div>}

      {/* ── Stat cards ──────────────────────────────────────────────────── */}
      <div className='adm-stat-row'>
        <StatCard
          label='Total Users'
          value={stats?.users.total.toLocaleString() ?? '—'}
          sub={`+${stats?.users.newToday ?? 0} today`}
        />
        <StatCard
          label='Jobs Queued'
          value={stats?.jobs.queued ?? '—'}
          sub={`${stats?.jobs.inProgress ?? 0} in progress`}
        />
        <StatCard
          label='Jobs Failed'
          value={stats?.jobs.failed ?? '—'}
          sub={`${stats?.jobs.deadLetter ?? 0} dead-letter`}
          alert={(stats?.jobs.failed ?? 0) > 0}
        />
        <StatCard
          label='Errors (1h)'
          value={stats?.errors.lastHour ?? '—'}
          sub={`${stats?.errors.last24h ?? 0} last 24h`}
          alert={(stats?.errors.lastHour ?? 0) > 0}
        />
        <StatCard
          label='Avg Latency (1h)'
          value={
            stats?.api.avgLatencyMs !== undefined
              ? `${stats.api.avgLatencyMs}ms`
              : '—'
          }
          sub={`${stats?.api.requestsLastHour ?? 0} requests`}
        />
        <StatCard
          label='AI Cost Today'
          value={
            stats?.aiCostTodayUsd !== undefined
              ? `$${stats.aiCostTodayUsd.toFixed(3)}`
              : '$0.000'
          }
        />
        <StatCard
          label='Transcriptions Today'
          value={stats?.transcription.successesToday ?? '—'}
          sub={`Tier1 ${stats?.transcription.tier1Today ?? 0} | Tier2 ${stats?.transcription.tier2Today ?? 0} | Tier3 ${stats?.transcription.tier3Today ?? 0}`}
        />
        <StatCard
          label='No Transcript Today'
          value={stats?.transcription.noTranscriptToday ?? '—'}
          sub={`${stats?.transcription.retryRequests24h ?? 0} admin retries (24h)`}
          alert={(stats?.transcription.noTranscriptToday ?? 0) > 0}
        />
        <StatCard
          label='Transcription Cost Today'
          value={
            stats?.transcription.totalCostTodayUsd !== undefined
              ? `$${stats.transcription.totalCostTodayUsd.toFixed(3)}`
              : '$0.000'
          }
          sub={`${stats?.transcription.avgExtractionMsToday ?? 0}ms avg extract`}
        />
      </div>

      {/* ── Charts ──────────────────────────────────────────────────────── */}
      <div className='adm-charts-row'>
        <div className='adm-chart-card'>
          <div className='adm-chart-title'>User Signups (7d)</div>
          <ResponsiveContainer
            width='100%'
            height={180}
          >
            <LineChart data={stats?.charts.signupsByDay ?? []}>
              <CartesianGrid
                strokeDasharray='3 3'
                stroke='#f0f0f0'
              />
              <XAxis
                dataKey='date'
                tickFormatter={shortDate}
                tick={{ fontSize: 11 }}
              />
              <YAxis
                tick={{ fontSize: 11 }}
                allowDecimals={false}
              />
              <Tooltip />
              <Line
                type='monotone'
                dataKey='count'
                stroke='#4f46e5'
                dot={false}
                strokeWidth={2}
                name='signups'
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className='adm-chart-card'>
          <div className='adm-chart-title'>Errors by Severity (7d)</div>
          <ResponsiveContainer
            width='100%'
            height={180}
          >
            <BarChart data={stats?.charts.errorsByDay ?? []}>
              <CartesianGrid
                strokeDasharray='3 3'
                stroke='#f0f0f0'
              />
              <XAxis
                dataKey='date'
                tickFormatter={shortDate}
                tick={{ fontSize: 11 }}
              />
              <YAxis
                tick={{ fontSize: 11 }}
                allowDecimals={false}
              />
              <Tooltip />
              <Legend iconSize={10} />
              <Bar
                dataKey='critical'
                stackId='a'
                fill='#ef4444'
              />
              <Bar
                dataKey='high'
                stackId='a'
                fill='#f59e0b'
              />
              <Bar
                dataKey='medium'
                stackId='a'
                fill='#3b82f6'
              />
              <Bar
                dataKey='low'
                stackId='a'
                fill='#9ca3af'
              />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className='adm-chart-card'>
          <div className='adm-chart-title'>API Requests (7d)</div>
          <ResponsiveContainer
            width='100%'
            height={180}
          >
            <LineChart data={stats?.charts.apiVolumeByDay ?? []}>
              <CartesianGrid
                strokeDasharray='3 3'
                stroke='#f0f0f0'
              />
              <XAxis
                dataKey='date'
                tickFormatter={shortDate}
                tick={{ fontSize: 11 }}
              />
              <YAxis
                tick={{ fontSize: 11 }}
                allowDecimals={false}
              />
              <Tooltip />
              <Legend iconSize={10} />
              <Line
                type='monotone'
                dataKey='count'
                stroke='#10b981'
                dot={false}
                strokeWidth={2}
                name='total'
              />
              <Line
                type='monotone'
                dataKey='errors'
                stroke='#ef4444'
                dot={false}
                strokeWidth={2}
                name='5xx errors'
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className='adm-chart-card'>
          <div className='adm-chart-title'>Transcription Cost (7d)</div>
          <ResponsiveContainer
            width='100%'
            height={180}
          >
            <LineChart data={stats?.charts.transcriptionCostByDay ?? []}>
              <CartesianGrid
                strokeDasharray='3 3'
                stroke='#f0f0f0'
              />
              <XAxis
                dataKey='date'
                tickFormatter={shortDate}
                tick={{ fontSize: 11 }}
              />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend iconSize={10} />
              <Line
                type='monotone'
                dataKey='costUsd'
                stroke='#8b5cf6'
                dot={false}
                strokeWidth={2}
                name='cost (USD)'
              />
              <Line
                type='monotone'
                dataKey='successes'
                stroke='#06b6d4'
                dot={false}
                strokeWidth={2}
                name='successes'
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className='adm-chart-card'>
          <div className='adm-chart-title'>Transcription Tier Mix (7d)</div>
          <ResponsiveContainer
            width='100%'
            height={180}
          >
            <BarChart data={stats?.charts.transcriptionTierByDay ?? []}>
              <CartesianGrid
                strokeDasharray='3 3'
                stroke='#f0f0f0'
              />
              <XAxis
                dataKey='date'
                tickFormatter={shortDate}
                tick={{ fontSize: 11 }}
              />
              <YAxis
                tick={{ fontSize: 11 }}
                allowDecimals={false}
              />
              <Tooltip />
              <Legend iconSize={10} />
              <Bar
                dataKey='tier1'
                stackId='t'
                fill='#22c55e'
                name='Tier 1'
              />
              <Bar
                dataKey='tier2'
                stackId='t'
                fill='#eab308'
                name='Tier 2'
              />
              <Bar
                dataKey='tier3'
                stackId='t'
                fill='#ef4444'
                name='Tier 3'
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ── Live panels ──────────────────────────────────────────────────── */}
      <div className='adm-live-row'>
        {/* Live error feed */}
        <div className='adm-live-panel'>
          <div className='adm-live-header'>
            <span className='adm-live-dot' />
            Live Error Feed
          </div>
          {liveErrors.length === 0 ? (
            <div className='adm-live-empty'>
              Waiting for errors — feed updates in real time
            </div>
          ) : (
            <ul className='adm-live-list'>
              {liveErrors.map((e, i) => (
                <li
                  key={`${e.id}-${i}`}
                  className='adm-live-item'
                >
                  <span className={`adm-severity adm-severity--${e.severity}`}>
                    {e.severity}
                  </span>
                  <span className='adm-live-msg'>{e.message}</span>
                  <span className='adm-live-cat'>{e.category}</span>
                  <span className='adm-live-time'>
                    {new Date(e.lastOccurrence).toLocaleTimeString()}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Job queue status */}
        <div className='adm-jobs-panel'>
          <div className='adm-live-header'>Job Queue</div>
          <div className='adm-jobs-grid'>
            {[
              {
                label: 'Queued',
                value: stats?.jobs.queued ?? 0,
                cls: 'secondary'
              },
              {
                label: 'In Progress',
                value: stats?.jobs.inProgress ?? 0,
                cls: 'primary'
              },
              {
                label: 'Failed',
                value: stats?.jobs.failed ?? 0,
                cls: 'danger'
              },
              {
                label: 'Dead Letter',
                value: stats?.jobs.deadLetter ?? 0,
                cls: 'dark'
              }
            ].map(({ label, value, cls }) => (
              <div
                key={label}
                className='adm-job-item'
              >
                <span className={`badge bg-${cls} adm-job-badge`}>{value}</span>
                <span className='adm-job-label'>{label}</span>
              </div>
            ))}
          </div>
          <Link
            to='/app/admin/ingestion'
            className='btn btn-sm btn-outline-secondary adm-jobs-link'
          >
            View all jobs →
          </Link>
        </div>
      </div>
    </div>
  );
}
